import React, {
  useContext,
  useState,
  useRef,
  FormEvent,
  useEffect,
} from "react";
import Button from "../../Components/Button";
import FormRow from "../../Components/FormRow";
import Label from "../../Components/Label";
import TextInput from "../../Components/TextInput";
import DataContext from "../../Context";
import { IPolicy } from "../../Models/PolicyInfo";
import "./Review.css";
import "../CompanyInfo/CompanyInfo.css";
import ValidationError from "../../Components/ValidationError";
import { FaPen, FaSave } from "react-icons/fa";
import InputMask from "react-input-mask";
import { API_ACTION, SaifFetch } from "../../Utils/SaifFetch";
import ButtonGroup from "../../Components/ButtonGroup";
import { useNavigate } from "react-router-dom";
import ReviewCredentials from "./ReviewCredentials";
import useSaifFetch from "../../hooks/useSaifFetch";
import { LoadingSpinner } from "@saif/loading-spinner";
import Confidentiality from "./Confidentiality/Confidentiality";
import { ILocalizationContext } from "../../Localization/Models/ILocalizationContext";
import LocalizationContext from "../../Localization/LanguageContext";
import AlertBanner from "../../Components/AlertBanner";

const Review = () => {
  const {
    email,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    username,
    setUsername,
    password,
    permPolicyClaim,
    permReportPay,
    permCertificates,
    permUMView,
    permUMFullAccess,
    permPolicyAdmin,
    policy,
    usernameValid,
    setUsernameValid,
    companyName,
    setCompanyName,
    yourTitle,
    setYourTitle,
    phoneNumber,
    setPhoneNumber,
    yourExtension,
    setYourExtension,
    setProgressState,
    fein,
    guid,
    question1,
    question1Answer,
    question2,
    question2Answer,
    question3,
    question3Answer,
    confViewed,
    setConfViewed,
    isSanitized,
  } = useContext(DataContext) as IPolicy;
  const { getValue, locale } = useContext(
    LocalizationContext
  ) as ILocalizationContext;

  const showButtons = useRef<HTMLInputElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLInputElement>(null);
  const scrollRefTop = useRef<HTMLInputElement>(null);
  const [profileEdit, setProfileEdit] = useState<boolean>(false);
  const [companyEdit, setCompanyEdit] = useState<boolean>(false);
  const [loginEdit, setLoginEdit] = useState<boolean>(false);
  const [isLoginValid, setIsLoginValid] = useState(true);
  const [body, setBody] = useState({});
  const [showBanner, setShowBanner] = useState<boolean>(false);
  const [agreeScrolled, setAgreeScrolled] = useState<boolean>(false);
  const { data, loading, status, error } = useSaifFetch<any>(
    API_ACTION.Activate_User,
    body,
    SaifFetch.post
  );

  let navigator = useNavigate();

  useEffect(() => {
    setProgressState(4);
    scrollRefTop.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "start",
    });
  }, []);

  useEffect(() => {
    if (status === 200) {
      navigator("/Confirmation", { replace: true });
    } else if (status === 400) {
      console.log(data);
      let invalidUsername = false;
      if ("username" in data) {
        invalidUsername = true;
        setLoginEdit(true);
        setUsernameValid((prev) => {
          return {
            invalid: invalidUsername,
            isShort: prev.isShort,
            touched: true,
            empty: prev.empty,
            nonLegitChars: prev.nonLegitChars,
          };
        });
        setLoginEdit(true);

        handleErrorCheck();
      }
    } else if ((status && status == 422) || error) {
      //password denided fo undetectable cause
      setShowBanner(true);
      bannerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "start",
      });
    } else if ((status && status >= 401) || error) {
      navigator("/error", { replace: true });
    }
  }, [status, error]);

  useEffect(() => {
    if (confViewed && IsDataValid() && !IsEditOpen()) {
      showButtons.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "start",
      });
    }
  }, [confViewed]);

  const handleEditSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  const handleSubmit = async () => {
    if (IsDataValid() && !IsEditOpen()) {
      let userData = {
        RegistrationKey: guid,
        PolicyNumber: parseInt(policy),
        firstName: firstName,
        lastName: lastName,
        username: username,
        password: password,
        companyName: companyName,
        phoneNumber: phoneNumber,
        phoneExtension: yourExtension.replace("x____", ""),
        emailAddress: email,
        title: yourTitle,
        SecurityQuestion1: question1.id,
        SecurityQuestion2: question2.id,
        SecurityQuestion3: question3.id,
        SecurityAnswer1: question1Answer,
        SecurityAnswer2: question2Answer,
        SecurityAnswer3: question3Answer,
        Language: locale,
      };

      setBody(userData);
    }
  };

  const ProfileEdit = () => {
    setProfileEdit(true);
  };

  const ProfileSave = () => {
    if (IsProfileValid()) setProfileEdit(false);
    setFirstName(firstName.trim());
    setLastName(lastName.trim());
    setUsername(username.trim());
  };

  const CompanyEdit = () => {
    setCompanyEdit(true);
  };

  const CompanySave = () => {
    if (IsCompanyValid()) setCompanyEdit(false);
    setYourTitle(yourTitle.trim());
    setCompanyName(companyName.trim());
  };

  const usernameTest = () => {
    if (username.length < 8 || username.length > 20 || !usernameValid) {
      return true;
      //also do the not in use check
    }
    return false;
  };

  const IsProfileValid = () => {
    if (
      !usernameTest() &&
      firstName.trim() &&
      lastName.trim() &&
      isSanitized(firstName) &&
      isSanitized(lastName)
    )
      return true;
  };

  const IsCompanyValid = () => {
    if (
      !companyName.trim() ||
      !yourTitle.trim() ||
      !isSanitized(companyName) ||
      !isSanitized(yourTitle)
    )
      return false;
    if (IsPhoneValid() === false) return false;
    return true;
  };

  const IsPhoneValid = () => {
    let unmasked = phoneNumber
      .replace("_", "")
      .replace("(", "")
      .replace(")", "")
      .replace("-", "")
      .replace(" ", "");
    if (unmasked.length !== 10) return false;
    return true;
  };

  const IsDataValid = () => {
    if (
      IsProfileValid() &&
      IsCompanyValid() &&
      IsPhoneValid() &&
      isLoginValid &&
      confViewed &&
      UserNameisValid()
    )
      return true;
    return false;
  };

  const IsEditOpen = () => {
    if (profileEdit || companyEdit || loginEdit) return true;
    return false;
  };

  const UserNameisValid = () => {
    return (
      usernameValid.invalid === false &&
      usernameValid.nonLegitChars === false &&
      usernameValid.isShort === false &&
      usernameValid.invalid === false
    );
  };

  const GoNext = () => {
    if (!IsDataValid()) {
      setTimeout(handleErrorCheck, 10);
      setAgreeScrolled(true);
    } else {
      handleSubmit();
    }
  };
  const DontGoBack = () => {
    setTimeout(handleErrorCheck, 10);
  };
  const GoBack = () => {
    setShowBanner(false);
    navigator("/CreateLogin", { replace: true });
  };

  const handleErrorCheck = () => {
    const input = document.querySelector(
      `.input-error,.check-error,.agreement-error,.question-required,.save-icon,.alert-error`
    );

    input?.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "start",
    });
  };

  const testharness = () => {
    setShowBanner(true);
    bannerRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "start",
    });
  };

  useEffect(() => {
    if (confViewed) {
      setAgreeScrolled(false);
    }
  }, [confViewed]);

  return (
    <div className="ReviewPage">
      {loading && (
        <LoadingSpinner
          className="loading"
          text={getValue("Review", "SpinnerCreating")}
        />
      )}
      <div id="scrollTop" ref={scrollRefTop} />
      <div className="container form-container px-4">
        <h2 className="content-title font-header-light text-4xl mb-1 review-header">
          {getValue("Review", "Title")}
        </h2>
        <form className="policy-info-form">
          <div className="base-row">
            <hr className="line-style" />
          </div>
          <FormRow>
            <Label>{getValue("Review", "PolicyLabel")}</Label>
            <p className="text-xs text-slate mb-2 px-0">
              {policy ? policy : "123456789"}
            </p>
          </FormRow>
          <FormRow>
            <Label>
              <i>{getValue("Review", "FEINLabelPrefix")}</i>
              {getValue("Review", "FEINLabelSuffix")}
            </Label>
            <p className="text-xs text-slate mb-2 px-0">
              {fein ? fein : "12-3456789"}
            </p>
          </FormRow>
          <FormRow>
            <Label>
              {getValue("Review", "EmailLabelPrefix")}
              <i>{getValue("Review", "EmailLabelSuffix")}</i>
            </Label>
            <p className="text-xs text-slate mb-2 px-0">{email}</p>
          </FormRow>
          <div className="base-row">
            <hr className="line-style" />
          </div>
        </form>
        <form onSubmit={handleEditSubmit} className="policy-info-form">
          <FormRow>
            <div className="edit-save-row">
              <div className="left-side-title">
                <h3 className="subtitle-left font-header-medium">
                  {getValue("Review", "ProfileHeading")}
                </h3>
              </div>
              <div className="right-side-title">
                <div className="button-spacer" />

                {profileEdit ? (
                  <div className="tab button-icon">
                    <Button
                      type="button"
                      version={IsProfileValid() ? "primary" : "disabled"}
                      bootstrap="col-2"
                      onClick={ProfileSave}
                      className="subtitle-right"
                    >
                      <FaSave className="save-icon" />
                      &nbsp;&nbsp;{getValue("Review", "Save")}
                    </Button>
                  </div>
                ) : (
                  <div className="tab button-icon">
                    <Button
                      type="button"
                      version="secondary"
                      bootstrap="col-2"
                      onClick={ProfileEdit}
                      className="subtitle-right"
                    >
                      <FaPen className="edit-icon" />
                      &nbsp;&nbsp;&nbsp;{getValue("Review", "Edit")}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </FormRow>
          <FormRow>
            <Label>{getValue("Review", "FirstNameLabel")}</Label>
            {profileEdit ? (
              <TextInput
                inputId="FirstName"
                inputValue={firstName}
                isInvalid={firstName.length < 1 || !isSanitized(firstName)}
                onChange={(e) => setFirstName(e.target.value)}
                errorMessage={getValue("Review", "ValFirstName")}
                maxLength={50}
              />
            ) : (
              <p className="text-xs text-slate mb-2 px-0">{firstName}</p>
            )}
          </FormRow>
          <FormRow>
            <Label>{getValue("Review", "LastNameLabel")}</Label>
            {profileEdit ? (
              <TextInput
                inputId="LastName"
                inputValue={lastName}
                isInvalid={lastName.length < 1 || !isSanitized(lastName)}
                onChange={(e) => setLastName(e.target.value)}
                errorMessage={getValue("Review", "ValLastName")}
                maxLength={50}
              />
            ) : (
              <p className="text-xs text-slate mb-2 px-0">{lastName}</p>
            )}
          </FormRow>
          <FormRow>
            <Label>{getValue("Review", "PermissionsLabel")}</Label>
            <>
              {permPolicyClaim && (
                <p className="text-xs text-slate mb-2 px-0">
                  {getValue("Review", "PermissionView")}
                </p>
              )}
              {permReportPay && (
                <p className="text-xs text-slate mb-2 px-0">
                  {getValue("Review", "PermissionPay")}
                </p>
              )}
              {permCertificates && (
                <p className="text-xs text-slate mb-2 px-0">
                  {getValue("Review", "PermissionCerts")}
                </p>
              )}
              {permUMView && (
                <p className="text-xs text-slate mb-2 px-0">
                  {getValue("Review", "UserManagementViewAccess")}
                </p>
              )}
              {permUMFullAccess && (
                <p className="text-xs text-slate mb-2 px-0">
                  {getValue("Review", "UserManagementFullAccess")}
                </p>
              )}
              {permPolicyAdmin && (
                <p className="text-xs text-slate mb-2 px-0">
                  {getValue("Review", "PolicyholderAdmin")}
                </p>
              )}
            </>
          </FormRow>
          <div className="base-row">
            <hr className="line-style" />
          </div>
        </form>
        <form onSubmit={handleEditSubmit} className="policy-info-form">
          <FormRow>
            <div className="edit-save-row">
              <div className="left-side-title">
                <h3 className="subtitle-left font-header-medium">
                  {getValue("Review", "CompanyHeading")}
                </h3>
              </div>
              <div className="right-side-title">
                <div className="button-spacer" />

                {companyEdit ? (
                  <div className="tab button-icon">
                    <Button
                      type="button"
                      version={IsCompanyValid() ? "primary" : "disabled"}
                      bootstrap="col-2"
                      onClick={CompanySave}
                      className="subtitle-right"
                    >
                      <FaSave className="save-icon" />
                      &nbsp;&nbsp;{getValue("Review", "Save")}
                    </Button>
                  </div>
                ) : (
                  <div className="tab button-icon">
                    <Button
                      type="button"
                      version="secondary"
                      bootstrap="col-2"
                      onClick={CompanyEdit}
                      className="subtitle-right"
                    >
                      <FaPen className="edit-icon" />
                      &nbsp;&nbsp;&nbsp;{getValue("Review", "Edit")}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </FormRow>
          <FormRow>
            <Label>{getValue("Review", "BusinessLabel")}</Label>
            {companyEdit ? (
              <TextInput
                inputId="CompanyName"
                inputValue={companyName}
                isInvalid={companyName.length < 1 || !isSanitized(companyName)}
                onChange={(e) => setCompanyName(e.target.value)}
                errorMessage={getValue("Review", "ValBusiness")}
                maxLength={64}
              />
            ) : (
              <p className="text-xs text-slate mb-2 px-0">{companyName}</p>
            )}
          </FormRow>
          <FormRow>
            <Label>{getValue("Review", "YourTitle")}</Label>
            {companyEdit ? (
              <TextInput
                inputId="YourTitle"
                inputValue={yourTitle}
                isInvalid={yourTitle.length < 1 || !isSanitized(yourTitle)}
                maxLength={50}
                onChange={(e) => setYourTitle(e.target.value)}
                errorMessage={getValue("Review", "ValYourTitle")}
              />
            ) : (
              <p className="text-xs text-slate mb-2 px-0">{yourTitle}</p>
            )}
          </FormRow>
          <FormRow>
            {!companyEdit ? (
              <Label>{getValue("Review", "PhoneLabel")}</Label>
            ) : (
              <></>
            )}

            {companyEdit ? (
              <>
                <div className="spread-header">
                  <Label>{getValue("Review", "PhoneLabel")}</Label>
                  <Label>{getValue("CompanyInfo", "ExtLabel")}</Label>
                </div>

                <InputMask
                  id="MaskedPhone"
                  className={
                    !IsPhoneValid()
                      ? "text-input p-2 input-error"
                      : "text-input p-2"
                  }
                  value={phoneNumber}
                  alwaysShowMask
                  mask="(999) 999-9999"
                  maskPlaceholder="(___) ___-____"
                  inputMode="numeric"
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
                <InputMask
                  id="MaskedExtension"
                  className="text-input w-25 p-2"
                  value={yourExtension}
                  alwaysShowMask
                  mask="x9999"
                  maskPlaceholder="x____"
                  inputMode="numeric"
                  onChange={(e) => setYourExtension(e.target.value)}
                />
                <ValidationError
                  isInvalid={!IsPhoneValid()}
                  message={getValue("Review", "ValPhone")}
                />
              </>
            ) : (
              <p className="text-xs text-slate mb-2 px-0">
                {phoneNumber}&nbsp;{yourExtension}
              </p>
            )}
          </FormRow>
          <div className="base-row">
            <hr className="line-style" />
          </div>
        </form>
        <div ref={bannerRef} id={"alert-banner"}></div>
        {showBanner && (
          <AlertBanner
            alertTextHeader={getValue("Review", "SystemFailureHeader")}
            alertText={
              getValue("Review", "SystemFailureBody1") +
              getValue("Review", "SystemFailureBody2")
            }
            testid="alert-password"
          />
        )}

        <ReviewCredentials
          edit={loginEdit || showBanner}
          setEdit={setLoginEdit}
          isLoginValid={isLoginValid}
          setIsLoginValid={setIsLoginValid}
          setShowBanner={setShowBanner}
        />
        <div className={`${agreeScrolled ? "agreement-error" : ""}`}>
          <Confidentiality
            confViewed={confViewed}
            setConfViewed={setConfViewed}
          />
        </div>
        <ButtonGroup>
          <span
            className="col-md-6 my-2"
            onClick={IsEditOpen() ? DontGoBack : undefined}
          >
            <Button
              type="button"
              bootstrap="col-12"
              onClick={!IsEditOpen() ? GoBack : DontGoBack}
              version={!IsEditOpen() ? "secondary" : "disabled"}
            >
              {getValue("Review", "Back")}
            </Button>
          </span>
          <span
            className="col-md-6 my-2"
            onClick={IsEditOpen() ? DontGoBack : undefined}
          >
            <Button
              type="submit"
              bootstrap="col-12"
              version={IsDataValid() && !IsEditOpen() ? "primary" : "disabled"}
              onClick={GoNext}
            >
              {getValue("Review", "Accept")}
            </Button>
          </span>
          <div ref={showButtons}></div>
        </ButtonGroup>
      </div>
      <div id="scrollbase" ref={scrollRef} />
    </div>
  );
};

export default Review;
